import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, AlertTriangle, DollarSign, Users, MapPin } from 'lucide-react';

const SEVERITY_STYLE = {
  light: { color: '#C9A84C', label: 'ROUTINE SEARCH', bg: 'rgba(201,168,76,0.08)' },
  medium: { color: '#FF6B35', label: 'ORGANIZED RAID', bg: 'rgba(255,107,53,0.08)' },
  heavy: { color: '#EF4444', label: 'TASK FORCE OPERATION', bg: 'rgba(239,68,68,0.1)' },
  federal: { color: '#DC2626', label: 'FEDERAL RICO SWEEP', bg: 'rgba(220,38,38,0.12)' },
};

const PoliceRaidPopup = ({ raidData, onDismiss }) => {
  const [dismissed, setDismissed] = useState(false);

  if (!raidData || dismissed) return null;

  const style = SEVERITY_STYLE[raidData.severity] || SEVERITY_STYLE.light;

  const handleDismiss = () => {
    setDismissed(true);
    setTimeout(() => onDismiss(), 300);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9500] flex items-center justify-center"
        data-testid="police-raid-popup"
      >
        <div className="absolute inset-0 bg-[#050505]/80" />

        {/* Red/blue flash */}
        <motion.div
          animate={{ opacity: [0, 0.2, 0, 0.15, 0] }}
          transition={{ duration: 1.5, repeat: 3, ease: 'linear' }}
          className="absolute inset-0 bg-red-600/10"
        />
        <motion.div
          animate={{ opacity: [0, 0.15, 0, 0.2, 0] }}
          transition={{ duration: 1.5, repeat: 3, ease: 'linear', delay: 0.4 }}
          className="absolute inset-0 bg-blue-600/10"
        />

        <motion.div
          initial={{ scale: 0.9, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          transition={{ delay: 0.2, type: 'spring', damping: 25 }}
          className="relative z-10 max-w-sm w-full mx-4"
        >
          <div className="rounded-xl overflow-hidden border" style={{ borderColor: style.color + '30', background: '#0a0e12' }}>
            {/* Header */}
            <div className="p-4 text-center" style={{ background: style.bg }}>
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1, repeat: 2 }}
              >
                <Shield size={32} style={{ color: style.color }} className="mx-auto mb-2" />
              </motion.div>
              <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#E8E0D0]">{style.label}</h3>
              <p className="font-['Outfit'] text-[9px] tracking-widest mt-1" style={{ color: style.color }}>
                THE BADGE — COMMISSIONER RODRIGUEZ
              </p>
            </div>

            {/* Narrative */}
            <div className="p-4 space-y-3">
              <p className="font-['Outfit'] text-xs text-[#E8E0D0]/60 leading-relaxed">{raidData.narrative}</p>

              {/* Consequences */}
              {raidData.consequences?.length > 0 && (
                <div className="space-y-1.5">
                  {raidData.consequences.map((c, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: style.color + '08', border: `1px solid ${style.color}20` }}>
                      {c.type === 'cash_seized' && <><DollarSign size={12} style={{ color: style.color }} /><span className="font-['Outfit'] text-[10px] text-[#E8E0D0]/60">Cash seized: ${c.amount?.toLocaleString()}</span></>}
                      {c.type === 'crew_arrested' && <><Users size={12} style={{ color: style.color }} /><span className="font-['Outfit'] text-[10px] text-[#E8E0D0]/60">Crew arrested: {c.crew_id} ({c.hours}h)</span></>}
                      {c.type === 'territory_seized' && <><MapPin size={12} style={{ color: style.color }} /><span className="font-['Outfit'] text-[10px] text-[#E8E0D0]/60">Territory seized: {c.name}</span></>}
                    </div>
                  ))}
                </div>
              )}

              {raidData.heat_gained > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 bg-[#DC2626]/5 border border-[#DC2626]/15 rounded-lg">
                  <AlertTriangle size={12} className="text-[#DC2626]" />
                  <span className="font-['Outfit'] text-[10px] text-[#DC2626]/70">+{raidData.heat_gained} HEAT from police attention</span>
                </div>
              )}

              <button onClick={handleDismiss}
                className="w-full py-2.5 border rounded-lg font-['Outfit'] text-[10px] tracking-widest transition-all hover:bg-[#E8E0D0]/5"
                style={{ borderColor: style.color + '30', color: style.color }}
                data-testid="raid-dismiss">
                CONTINUE
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PoliceRaidPopup;
